import React, { useEffect, useState } from "react";
import { StorebrandKeycloak } from "@storebrand-digital/storebrand-auth";
import useAuthTimeout from "../../utils/useAuthTimeout";

import { ReactComponent as LogoutIcon } from "../../static/img/logout.svg";
import Button from "@storeblocks/button";
import Dialog from "../Dialog";
import ButtonWithIcon from "../ButtonWithIcon";
import { ButtonContainer } from "./styled";

/** AuthTimeoutHandler.
 *
 *  Simple utility to keep tabs of when user becomes inactive.
 *  Uses local storage to keep track of idle time in order to enable
 *  it to work across multiple tabs.
 */

const AuthTimeoutHandler = () => {
  const { logout } = StorebrandKeycloak;

  const {
    expiryTimeReached,
    warningTimeReached,
    forceTokenUpdate,
    setWarningTimeReached,
  } = useAuthTimeout();

  const [showWarningDialog, setShowWarningDialog] =
    useState<boolean>(warningTimeReached);

  const [showExpiredDialog, setShowExpiredDialog] =
    useState<boolean>(expiryTimeReached);

  const handleContinueClicked = () => {
    setShowWarningDialog(false);
    setWarningTimeReached(false);

    forceTokenUpdate();
  };

  const handleLoginClicked = async () => {
    setShowExpiredDialog(false);
    // Temporary solution to ensure login works.
    StorebrandKeycloak.getInstance()?.clearToken();
    location.reload();
  };

  useEffect(() => {
    if (warningTimeReached === true) {
      setShowWarningDialog(true);
    }
  }, [warningTimeReached]);

  useEffect(() => {
    if (expiryTimeReached === true) {
      setShowWarningDialog(false);
      setShowExpiredDialog(true);
    }
  }, [expiryTimeReached]);

  const renderWarningDialog = () => {
    return (
      <Dialog isOpen={showWarningDialog} title="Du blir snart logget ut">
        <>
          <p>Hei!</p>
          <p>
            Vi ser at du har vært inaktiv en stund, og du vil derfor bli logget
            ut om fem minutter. Hvis du vil fortsette nå, klikk på{" "}
            <strong>Jeg vil fortsette</strong> under.
          </p>
          <ButtonContainer>
            <Button onClick={handleContinueClicked}>Jeg vil fortsette</Button>
            <ButtonWithIcon
              icon={LogoutIcon}
              iconPosition="right"
              onClick={() => logout()}
              variant="transparent"
            >
              Jeg vi logge ut
            </ButtonWithIcon>
          </ButtonContainer>
        </>
      </Dialog>
    );
  };

  const renderExpiredDialog = () => {
    return (
      <Dialog isOpen={showExpiredDialog} title="Du har blitt logget ut">
        <>
          <p>Hei!</p>
          <p>
            Du har vært inaktiv over en lengre periode, og vi har derfor logget
            deg ut. Logg inn på nytt for å fortsette.
          </p>
          <ButtonContainer>
            <Button onClick={() => handleLoginClicked()}>Logg inn</Button>
          </ButtonContainer>
        </>
      </Dialog>
    );
  };

  return (
    <>
      {warningTimeReached && showWarningDialog && renderWarningDialog()}
      {expiryTimeReached && showExpiredDialog && renderExpiredDialog()}
    </>
  );
};

export default AuthTimeoutHandler;
